import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, ArrowRight, Sparkles } from "lucide-react";

/**
 * Bottom-of-listing CTA strip — captures organic SEO visitors who land on a
 * listing and might themselves be a local business owner ready to claim
 * their own Best of Best Listing.
 *
 * IMPORTANT: We deliberately do NOT echo the listing's own category here —
 * the page owner is trying to stand out as the best in their category, so we
 * keep the recruit-message generic ("Run a business") to avoid surfacing
 * competitor sign-ups on the same listing.
 *
 * The CTA appends ?ref={referrerSlug} so the referring listing earns affiliate
 * credit when the new business eventually upgrades to a paid plan.
 */
export default function GetYourListingStrip({ city, state, referrerSlug }) {
  const locationText = city ? city : "your area";
  const submitUrl = referrerSlug ? `/submit?ref=${encodeURIComponent(referrerSlug)}` : "/submit";

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="rounded-2xl overflow-hidden border border-[#F59E0B]/30 bg-gradient-to-r from-[#7C3AED]/15 via-[#0F172A] to-[#F59E0B]/10"
      data-testid="get-your-listing-strip"
    >
      <div className="px-5 py-4 sm:px-6 sm:py-5 flex items-center gap-3 sm:gap-5 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#EC4899] flex items-center justify-center flex-shrink-0">
          <Award className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <p className="text-white font-semibold text-sm sm:text-base leading-tight">
            Run a business in <span className="text-[#FBBF24]">{locationText}{state ? `, ${state}` : ""}</span> and surrounding areas?
          </p>
          <p className="text-[#94A3B8] text-xs sm:text-sm mt-1">
            I would like to recommend you for your own LocalBest.ai Listing! Claim your{" "}
            <strong className="text-white">free Best of Best Listing</strong> on LocalBest.ai — AI-powered profile, free leads, and your own digital business card.
          </p>
        </div>
        <Link
          to={submitUrl}
          className="inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 rounded-full bg-gradient-to-r from-[#F59E0B] to-[#EC4899] hover:opacity-90 text-white font-semibold text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F59E0B]/50"
          data-testid="get-your-listing-cta"
        >
          <Sparkles className="w-4 h-4" />
          Get My Free Listing
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </motion.section>
  );
}
